import React, { useContext, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";

import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    height: "calc(100% - 58px)",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.boxlist,
  },

  chatList: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  listItem: {
    cursor: "pointer",
    borderLeft: "3px solid transparent",
    transition: "background-color 0.1s, border-color 0.1s",
  },

  listItemSelected: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.type === "light"
      ? "rgba(45, 123, 232, 0.1)"
      : "rgba(45, 123, 232, 0.15)",
  },

  chatActions: {
    display: "flex",
    gap: 2,
    opacity: 0,
    transition: "opacity 0.15s",
    "$listItem:hover &": {
      opacity: 1,
    },
    "$listItemSelected &": {
      opacity: 1,
    },
  },

  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: 38,
    height: 38,
    fontSize: "0.9rem",
    fontWeight: 700,
  },

  chatTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  chatPreview: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.disabled,
    flex: 1,
    gap: theme.spacing(1),
  },

  emptyIcon: {
    fontSize: 48,
    opacity: 0.3,
    marginBottom: theme.spacing(0.5),
  },
}));

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  const { id } = useParams();

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
      handleSelectChat(chat);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users.find((u) => u.userId === user.id);
    return currentUser.unreads;
  };

  const getInitials = (title) =>
    title
      ? title.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
      : "?";

  return (
    <>
      <ConfirmationModal
        title="Excluir Conversa"
        open={confirmationModal}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser revertida, confirmar?
      </ConfirmationModal>

      <div className={classes.mainContainer}>
        <div className={classes.chatList}>
          {Array.isArray(chats) && chats.length === 0 && !loading && (
            <div className={classes.emptyState}>
              <ChatBubbleOutlineIcon className={classes.emptyIcon} />
              <Typography variant="body2">Nenhuma conversa ainda.</Typography>
              <Typography variant="caption">
                Clique em "Nova" para começar.
              </Typography>
            </div>
          )}

          <List disablePadding>
            {Array.isArray(chats) &&
              chats.map((chat, key) => {
                const unreads = unreadMessages(chat);
                const isSelected = chat.uuid === id;

                return (
                  <ListItem
                    onClick={() => goToMessages(chat)}
                    key={key}
                    className={`${classes.listItem}${isSelected ? ` ${classes.listItemSelected}` : ""}`}
                    button
                    divider
                  >
                    <ListItemAvatar>
                      <Avatar className={classes.avatar}>
                        {getInitials(chat.title)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                          <span className={classes.chatTitle}>{chat.title}</span>
                          {unreads > 0 && (
                            <Chip
                              size="small"
                              label={unreads}
                              color="primary"
                              style={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography className={classes.chatPreview}>
                          {chat.lastMessage || "Sem mensagens"}
                        </Typography>
                      }
                    />

                    {chat.ownerId === user.id && (
                      <ListItemSecondaryAction className={classes.chatActions}>
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={() => {
                              goToMessages(chat).then(() => {
                                handleEditChat(chat);
                              });
                            }}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedChat(chat);
                              setConfirmModalOpen(true);
                            }}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
          </List>
        </div>
      </div>
    </>
  );
}
